import { getAuthContext } from './authContext'
import { invalidateAuthCache } from './authValidationCache'
import { invalidateAdminAuthCache } from '../utils/adminAuthCache'

const SESSION_PREFERENCE_KEYS = ['app_local_theme', 'app_locale'] as const
const TOKEN_REFRESH_GRACE_MS = 60 * 60 * 1000
let authSessionInitialized = false
let authSessionStartedWithValidToken = false

export const isLoginRequest = (url?: string) => {
  if (!url) return false
  return url.includes('/admins/login') || url.includes('/login')
}

export const isRefreshTokenRequest = (url?: string) => {
  if (!url) return false
  return url.includes('/admins/refresh-token')
}

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const [, payload = ''] = token.split('.')
    if (!payload) return null

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return null
  }
}

/**
 * Returns the active token.
 * Priority order:
 * 1. impersonation_token from sessionStorage (impersonation sessions)
 * 2. admin_token from sessionStorage (agent direct login)
 * 3. admin_token from localStorage (admin login)
 */
export const getActiveToken = (): string | null => {
  const impersonationToken = sessionStorage.getItem('impersonation_token')
  if (impersonationToken) {
    return impersonationToken
  }

  const sessionToken = sessionStorage.getItem('admin_token')
  if (sessionToken) {
    return sessionToken
  }

  return localStorage.getItem('admin_token')
}

export const initAuthSessionFromStorage = () => {
  const token = getActiveToken()
  authSessionInitialized = true
  authSessionStartedWithValidToken = Boolean(token && !isTokenExpiredBeyondRefreshGrace(token))
}

export const markAuthSessionActive = () => {
  authSessionInitialized = true
  authSessionStartedWithValidToken = true
}

export const canRefreshActiveToken = () => {
  return authSessionInitialized && authSessionStartedWithValidToken
}

export const persistActiveToken = (token: string) => {
  const { type } = getAuthContext()

  invalidateAuthCache()
  invalidateAdminAuthCache()

  if (type === 'impersonation') {
    sessionStorage.setItem('impersonation_token', token)
    return
  }

  if (type === 'agent') {
    sessionStorage.setItem('admin_token', token)
    return
  }

  localStorage.setItem('admin_token', token)
}

const clearSessionPreferences = () => {
  SESSION_PREFERENCE_KEYS.forEach((key) => sessionStorage.removeItem(key))
}

export const clearAuthSession = () => {
  authSessionStartedWithValidToken = false
  invalidateAuthCache()
  invalidateAdminAuthCache()

  const { isImpersonating, isAgentDirectLogin } = getAuthContext()

  if (isImpersonating) {
    sessionStorage.removeItem('impersonation_session')
    sessionStorage.removeItem('impersonation_token')
    clearSessionPreferences()
    return
  }

  if (isAgentDirectLogin) {
    sessionStorage.removeItem('admin_token')
    sessionStorage.removeItem('role')
    clearSessionPreferences()
    return
  }

  localStorage.removeItem('admin_token')
  localStorage.removeItem('role')
  localStorage.removeItem('user')
  localStorage.removeItem('impersonation_was_used')
}

export const forceLogout = () => {
  const { isImpersonating } = getAuthContext()

  if (isImpersonating) {
    clearAuthSession()
    window.location.replace('/auth/agent-logout')
    return
  }

  clearAuthSession()
  window.location.replace('/auth/login')
}

export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJwtPayload(token)
  const exp = payload?.exp
  if (typeof exp !== 'number') return false
  return Date.now() >= exp * 1000
}

export const isTokenExpiredBeyondRefreshGrace = (token: string): boolean => {
  const payload = decodeJwtPayload(token)
  const exp = payload?.exp
  if (typeof exp !== 'number') return false

  return Date.now() - exp * 1000 >= TOKEN_REFRESH_GRACE_MS
}
